import * as React from 'react';
import SearchInput from '../SearchInput';
import { debounce } from '../debounce';
import { ArticleSuggestion } from './types/ArticleSuggestion';
import { LoadingIndicator } from '../LoadingIndicator';
import { Alert } from 'ps-design';
import { Article } from './Article';
import './style/articleSearchFullPage.scss';

interface ArticleSearchProps {
    articles: ArticleSuggestion[];
    logoPath?: string;
    searchImagePath?: string;
    searchTermCallBack(value: string): Promise<void>;
}

interface ArticleSearchState {
    isLoading: boolean;
    searchValue: string;
    hasSearched: boolean;
    showArticle: string;
    showArticleHeader: string;
    hasError: boolean;
}

export class ArticleSearchFullPage extends React.Component<ArticleSearchProps, ArticleSearchState> {
    private _minSearchTermLength: number = 4;
    constructor(props: ArticleSearchProps) {
        super(props);
        // if we are given one article at time of construction that means the user has just arrived
        // via a callback after selecting an article on the contact form and wants to view the article
        // so make sure to set the article to be shown
        const showArticle = this.props.articles.length === 1 ?
            this.props.articles[0].content : '';
        const showArticleHeader = this.props.articles.length === 1 ?
            this.props.articles[0].title : '';
        this.state = {
            isLoading: false,
            searchValue: '',
            showArticleHeader,
            hasSearched: false,
            showArticle,
            hasError: false
        };
        this.salesForceCallback = debounce(this.salesForceCallback.bind(this), 250);
        this.onChangeSearch = this.onChangeSearch.bind(this);
        this.goToArticle = this.goToArticle.bind(this);
        this.handleErrorBannerClose = this.handleErrorBannerClose.bind(this);
    }

    render(): React.ReactNode {
        const { logoPath } = this.props;
        return (
            <div className="article-search-full-page">
                <div className="article-search-full-page__header">
                    {logoPath && <img src={logoPath} className="payscale-logo" alt=""/>}
                    <div className="article-search-full-page__header-content">
                        <h2 className="article-search-full-page__support-header">
                            Support help
                        </h2>
                        <SearchInput
                            onChange={this.onChangeSearch}
                            placeholderText="Search Support Help"
                        />
                    </div>
                </div>
                {this.state.hasError && this.renderErrorBanner()}
                <div className="suggested-articles">
                    {(!this.state.isLoading &&
                        !this.state.showArticle &&
                        this.props.articles.length > 0) &&
                        <div className="suggested-articles__header">
                            {this.renderHeaderText()}
                        </div>
                    }
                    {this.renderListBody()}
                </div>
                {this.state.showArticle && !this.state.isLoading &&
                    <div className="article-search-full-page__footer">
                        <p className="article-search-full-page__footer-text">
                            Was this article helpful?
                        </p>
                        {/* Buttons hooked up through Pendo */}
                        <button className="pxl-btn article-search-full-page__yes-btn">
                            Yes
                        </button>
                        <button className="pxl-btn article-search-full-page__no-btn">
                            No
                        </button>
                    </div>
                }
            </div>
        );
    }

    renderListBody(): React.ReactNode {
        const { searchImagePath } = this.props;
        if (this.state.isLoading) {
            return (
                <div className="suggested-articles__loading">
                    <LoadingIndicator />
                    <div className="suggested-articles__loading-text">
                        Searching Articles...
                    </div>
                </div>
            );
        }
        if (!this.props.articles.length && !this.state.isLoading && this.state.hasSearched) {
            return (
                <div className="suggested-articles__no-articles">
                    {searchImagePath && <img src={searchImagePath} className="search-image" alt=""/>}
                    <h3 className="suggested-articles__no-articles-header">
                        No Articles Found
                    </h3>
                    <div className="suggested-articles__no-articles-body">
                        Try different keywords or more general keywords
                    </div>
                </div>
            );
        }
        if (this.state.showArticle) {
            return (
                <div className="salesforce-html">
                    <h2 className="salesforce-html__header">
                        {this.state.showArticleHeader}
                    </h2>
                    <div
                        className="salesforce-html__body"
                        dangerouslySetInnerHTML={{
                            __html: this.state.showArticle
                        }}
                    />
                </div>
            );
        }
        return (
            <ul className="suggested-articles__articles">
                {this.props.articles.map(article => {
                    return this.renderArticleLink(article);
                })}
            </ul>
        );
    }

    renderArticleLink(article: ArticleSuggestion): React.ReactNode {
        return (
            <Article article={article} goToArticle={this.goToArticle} key={`article-${article.id}`} />
        );
    }

    renderHeaderText(): string | null {
        const { showArticle } = this.state;
        const { articles } = this.props;
        if (articles.length && !showArticle) {
            return `Search Results (${articles.length})`;
        }
        return null;
    }

    renderErrorBanner(): React.ReactNode {
        return (
            <Alert
                className="article-search-full-page__error-banner"
                message="Failed to sumbit the request. Please try again or refresh the page."
                level="danger"
                close={true}
                onClickClose={this.handleErrorBannerClose}
            />
        );
    }

    onChangeSearch(value: string): void {
        this.setState({searchValue: value});
        if (value.length < this._minSearchTermLength) {
            return;
        }
        this.salesForceCallback(value);
    }

    async salesForceCallback(value: string): Promise<void> {
        this.setState({isLoading: true});
        try {
            await this.props.searchTermCallBack(value);
        } catch {
            this.setState({hasError: true});
        }
        this.setState({isLoading: false, hasSearched: true, showArticle: '', showArticleHeader: ''});
    }

    goToArticle(id: string): void {
        window.location.search = `id=${id}`;
    }

    handleErrorBannerClose(): void {
        this.setState({hasSearched: false, hasError: false, searchValue: ''});
    }
}

export default { ArticleSearchFullPage };